import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAssetAnalayticsForContact,
  fetchContact,
  fetchContactSections,
  fetchContentsForShare,
  fetchSharesForContact,
  invalidateContactUpdations,
  updateContact,
} from "src/redux/actions/contact";
import { Asset } from "src/types/assets";
import userRoleService from "src/services/userRoles.service";
import { Contact as ContactType, ContactShare } from "src/types/contact";
import pluralize from "pluralize";
import TimeAgo from "react-timeago";
import {
  getEmailOpenEvent,
  getViewEvent,
} from "src/services/conversationMetrics.service";
import dateformat from "dateformat";
import useMeasure from "react-use-measure";
import { useSpring, animated } from "@react-spring/web";
import { Link } from "react-router-dom";

import { Image } from "react-bootstrap";
import Tooltip from "../tooltip";
import InfiniteScroll from "../infiniteScroll";
import AssetShare from "./components/assetShare";
import { SectionShare } from "./components/sectionShare";
import ContactForm from "./components/form";
import Slider from "../slider";
import {
  AssetShareLoader,
  ContactLoader,
  ShareLoader,
} from "src/pages/conversations/components/loaders";
import Panel from "src/pages/asset/components/animation/panel";

import _ from "lodash";
import cx from "classnames";
import styles from "./contact.module.scss";
interface IContact {
  show: boolean;
  contactId: string;
  onClose: () => void;
}

const Contact: React.FC<IContact> = ({ show, contactId, onClose }) => {
  //constants
  const dispatch = useDispatch();

  //effects
  useEffect(() => {
    if (contactId) {      
      dispatch(fetchContact({ id: contactId }));
      dispatch(fetchSharesForContact({ id: contactId, page: 0 }));
    }
  }, [contactId]);

  //selectors
  const { loading, contact } = useSelector((state: any) => {
    return {
      loading: state.contact.loading,
      contact: state.contact.contact,
    };
  });

  //states
  const [edit, setEdit] = React.useState(false);
  const [cropView, setCropView] = React.useState(false);

  //functions
  const handleUpdateContact = (contact: ContactType) => {
    dispatch(
      updateContact({
        id: contactId,
        request: contact,
      }),
    );
  };

  //render
  return (
    <Slider.Dialog
      show={show}
      onClose={() => {}}
      width={cropView ? "400px" : "620px"}
    >
      <Slider.Body className={styles.sliderBody}>
        {loading && <ContactLoader />}

        <div className={styles.container}>
          {!loading && contact && (
            <div className={cx(styles.contactOverview)}>
              <Panel
                show={!edit}
                className={styles.editPanel}
                config={{
                  initial: { opacity: 0, left: "0%" },
                  from: { opacity: 0, left: "-50%" },
                  enter: { opacity: 1, left: "0%" },
                  leave: { opacity: 0, left: "-50%" },
                }}
              >
                <ContactOverview
                  contactId={contactId}
                  toggleEdit={() => setEdit(true)}
                  onClose={onClose}
                />
              </Panel>
            </div>
          )}
          {contact && (
            <Panel
              show={edit}
              className={styles.editPanel}
              config={{
                from: { opacity: 0, right: "-50%" },
                enter: { opacity: 1, right: "0%" },
                leave: { opacity: 0, right: "-50%" },
              }}
            >
              <ContactForm
                contact={contact}
                invalidateUpdationStatus={() => dispatch(invalidateContactUpdations())}
                onSave={handleUpdateContact}
                toggleShow={() => setEdit(false)}
                toggleCropView={(crop) => setCropView(crop)}
              />
            </Panel>
          )}
        </div>
      </Slider.Body>
    </Slider.Dialog>
  );
};

export default Contact;

interface IContactOverView {
  contactId: string;
  onClose: () => void;
  toggleEdit: () => void;
}

const ContactOverview: React.FC<IContactOverView> = ({
  contactId,
  onClose,
  toggleEdit,
}) => {
    //selectors
    const { loadingShares, pagination, contact, shares } = useSelector(
      (state: any) => {
        return {
          loading: state.contact.loading,
          contact: state.contact.contact,
          shares: state.contact.shares.items,
          loadingShares: state.contact.shares.loading,
          pagination: state.contact.shares.pagination,
        };
      },
    );
    
  //constants
  const dispatch = useDispatch();
  const hasMore = pagination.totalPages > pagination.currentPage;

  //functions  
  const renderProfileImage = () => {
    const icon = _.get(contact, "icon.thumbnail");
    if (icon) {
      return <Image className={styles.profileImage} src={icon} roundedCircle />;
    } else {
      return (
        <div className={styles.profileImage}>
          <span>{(contact.firstName || "")[0]}</span>
          <span>{(contact.lastName || "")[0]}</span>
        </div>
      );
    }
  };

  const renderSource = (source: string) => {
    switch (source) {
      case "quicksend":
        return "Quick Send";
      case "generate_link":
        return "Generate Link";
      case "lead":
        return "Lead";
      case "reshare":
        return "Reshare";
      default:
        return source;
    }
  };

  //render
  return (
    <>
      <div className={styles.header}>
        <div className={styles.profileInfo}>
          <div className={styles.avatarContainer}>
            {renderProfileImage()}
            {userRoleService.canCreateContact() && (
              <span className={styles.editIcon} onClick={toggleEdit}>
                <i className={cx("paperflite-edit", styles.editIcon)} /> Update
              </span>
            )}
          </div>
          <div className={styles.contactDetails}>
            <p className={styles.name}>
              <>
                {contact.firstName || ""} {contact.lastName || ""}
                {contact.doNotDisturb && (
                  <span className={styles.tag}>DND</span>
                )}
              </>
            </p>

            <div className={styles.contactInfo}>
              {contact.email && (
                <span className={styles.contact}>{contact.email}</span>
              )}
              {contact.company && (
                <span className={styles.contact}>{contact.company}</span>
              )}

              {contact.title && (
                <span className={styles.contact}>{contact.title}</span>
              )}

              {contact.mobilePhone && (
                <span className={styles.contact}>
                  Mobile: {contact.mobilePhone}
                </span>
              )}

              {contact.workPhone && (
                <span className={styles.contact}>
                  Work: {contact.workPhone}
                </span>
              )}
            </div>

            <div className={styles.tags}>
              {_.map(contact.sources, (tag) => (
                <span key={tag} className={styles.tag}>
                  {renderSource(tag)}
                </span>
              ))}
            </div>
          </div>
        </div>

        <i className={cx("paperflite-close", styles.closeIcon)} onClick={onClose}></i>
      </div>

      <div className={styles.body}>
        <div className={styles.sharesList}>
          <h3 className={styles.conversationActivity}>conversation activity</h3>
          <div className={styles.shares}>
            <InfiniteScroll
              loadMore={() => {
                dispatch(
                  fetchSharesForContact({
                    id: contactId,
                    page: pagination.currentPage + 1,
                  }),
                )
              }}
              hasMore={hasMore}
              loading={loadingShares}
              loader={() => (
                <ShareLoader count={!pagination.currentPage ? 3 : 1} />
              )}
            >
              {shares.map((share: ContactShare) => (
                <Share key={share.id} share={share} contactId={contactId} />
              ))}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    </>
  );
};

interface IShare {
  share: ContactShare;
  contactId: string;
}

const Share: React.FC<IShare> = ({ share, contactId }) => {
  //selectors
  const user = useSelector((state: any) => state.users.me);

  //constants
  const dispatch = useDispatch();
  const emailEvent = getEmailOpenEvent(share.events, share.source);
  const viewEvent = getViewEvent(share.events);
  const collectionReshare = share.events.find(
    (event: any) => event.type === "collection_reshared",
  );

  const renderSharedBy = () => {
    const sharedById = _.get(share, "createdBy.id", "");
    let name = "";
    if (sharedById === user.id) {
      name = "me";
    } else {
      return (name =
        _.get(share, "createdBy.firstName", "") +
        _.get(share, "createdBy.lastName", ""));
    }

    return (
      <span>
        shared <TimeAgo date={share.sharedDate} /> by  {name}
      </span>
    );
  };

  //states
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  //animations
  const [childrenAnimation] = useSpring(
    {
      opacity: mountChildren ? 1 : 0,
      height: mountChildren ? containerBounds.height : 0,
      from: { opacity: 0, height: 0 },
    },
    [mountChildren, containerBounds],
  );

  //functions
  const handelShareClick = () => {
    setMountChildren(!mountChildren);
    if (share.fetchedChildren) {
      return;
    }
    dispatch(
      fetchContentsForShare({
        shareId: share.id,
        contactId,
      }),
    );
  };

  const fetchAnalytics = (assetId: string, sectionId?: string) => {
    dispatch(
      fetchAssetAnalayticsForContact({
        shareId: share.id,
        contactId,
        assetId,
        sectionId,
      }),
    );
  };

  const fetchAssetsForSection = (sectionId: string) => {
    dispatch(
      fetchContactSections({
        shareId: share.id,
        contactId,
        sectionId,
      }),
    );
  };

  //render
  return (
    <div className={styles.shareContainer}>
      <div className={styles.share}>
        <div className={styles.shareDetails}>
          <span className={cx(styles.shareName, {
            [styles.expanded] : mountChildren
          })} onClick={handelShareClick}>
            <p>{_.get(share, "name") || "(No Subject)"}</p>
            <i className="paperflite-dropdown" />
          </span>

          <p className={styles.sharedBy}>{renderSharedBy()}</p>
          <span className={styles.assetDetails}>
            <span className={styles.assetCount}>
              {share.assetsCount + " " + pluralize("asset", share.assetsCount)}
            </span>
            <span className={styles.source}>{share.source}</span>
          </span>
        </div>
        <div className={styles.shareActions}>
          <div className={styles.analytics}>
            <span
              className={cx(styles.metric, {
                [styles.disabled]: !emailEvent,
              })}
            >
              <Tooltip
                content={
                  emailEvent
                    ? ` email opened on ${dateformat(emailEvent.createdDate, "mmm dd, yyyy")}`
                    : ""
                }
                placement="top"
              >
                <i className="paperflite-mail-open" />
              </Tooltip>
            </span>

            <span
              className={cx(styles.metric, {
                [styles.disabled]: !viewEvent,
              })}
            >
              <Tooltip
                content={
                  viewEvent
                    ? `last clicked on ${dateformat(viewEvent.createdDate, "mmm dd, yyyy")}`
                    : ""
                }
                placement="top"
              >
                <i className="paperflite-click-rate" />
              </Tooltip>
            </span>

            <span
              className={cx(styles.metric, {
                [styles.disabled]: !collectionReshare,
              })}
            >
              <Tooltip
                content={
                  collectionReshare
                    ? `Reshared ${collectionReshare?.count} times, last on ${dateformat(collectionReshare?.createdDate, "mmm dd yyyy")}`
                    : ""
                }
                placement="top"
              >
                <i className="paperflite-share" />
              </Tooltip>
            </span>
          </div>

          <Link
            to={`/conversations/${share.id}`}
            className={cx(styles.cta, {
              [styles.showCta]: mountChildren,
            })}
          >
            <i className="paperflite-right-arrow" />
            Go to conversation
          </Link>
        </div>
      </div>

      <animated.div style={childrenAnimation}>
        {mountChildren && (
          <div
            ref={containerRef}
            className={cx(styles.children, {
              [styles.fetching]: _.get(share, "fetchingChildren"),
            })}
          >
            {
              <div className={styles.sections}>
                {_.get(share, "fetchingChildren") && (
                  <AssetShareLoader count={2} />
                )}
                {_.get(share, "fetchedChildren") &&
                  _.map(share.sections, (section) => (
                    <SectionShare
                      key={section.id}
                      section={section}
                      fetchAssetsForSection={fetchAssetsForSection}
                      fetchAnalytics={fetchAnalytics}
                      source={"contact"}
                    />
                  ))}
              </div>
            }
            {(
              (_.size(share.sections) === 0 && _.get(share, "assets", [])) ||
              []
            ).map((asset: Asset) => (
              <AssetShare
                key={asset.id}
                asset={asset}
                source="contact"
                fetchAnalytics={fetchAnalytics}
              />
            ))}

            {
              _.get(share, "fetchedChildren") && _.size(share.assets) === 0 && _.size(share.sections) === 0 && (
                <div className={styles.noRecords}>
                  <p>No records found</p>
                </div>
              )
            }
          </div>
        )}
      </animated.div>
    </div>
  );
};
